import React, { useEffect, useState } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import styled from "styled-components";
import { SelectPicker, Message, InputNumber, useToaster, Toggle } from "rsuite";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { DateTime } from "luxon";
import {
  AlertModal,
  SeverityAlert,
  PreviewHeader,
  Form,
  Button,
} from "../../../../ui";

export const AddAlertFC = ({
  dataConfig,
  dataTable,
  sme,
  refresh,
  metadata,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormSite = {
    user_id: "",
    system_id: sme,
    field_name: "",
    operator: "",
    threshold: "",
    threshold_units: "",
    threshold_offset: "",
    severity: "",
    notify_frequency: "",
    pg_table_switch: "",
  };

  let fieldSelector = null;

  if (metadata.manufacturer === "Philips") {
    fieldSelector = Object.keys(dataTable)
      .filter((key) =>
        [
          "helium_level_value",
          "he_psi_avg_value",
          "capture_datetime",
          "he_level_1_value",
          "he_level_2_value",
          "mag_psia_value",
          "comp_vib_status",
          "shield_temp_value",
          "host_datetime",
        ].includes(key),
      )
      .map((operator) => ({ label: operator, value: operator }));
  } else {
    fieldSelector = Object.keys(dataTable)
      .filter((key) =>
        [
          "he_level_value",
          "helium_level_value",
          "he_psi_avg_value",
          "capture_datetime",
          "he_level_1_value",
          "he_level_2_value",
          "mag_psia_value",
          "he_pressure_value",
          "comp_vib_status",
          "shield_temp_value",
          "host_datetime",
        ].includes(key),
      )
      .map((operator) => ({ label: operator, value: operator }));
  }

  fieldSelector.push({ label: "dynamic", value: "dynamic" });
  const [operatorSelector, setOperatorSelector] = useState(
    [
      "less_than",
      "greater_than",
      "equals",
      "delta_neg",
      "offline",
      "frozen_host",
      "absent_null_data",
    ].map((operator) => ({
      label: operator.replace(/_/g, " "),
      value: operator,
    })),
  );
  const severitySelector = ["high", "medium"].map((operator) => ({
    label: operator,
    value: operator,
  }));
  const pgTableSelector = ["MAGNET", "EDU"].map((operator) => ({
    label: operator,
    value: operator,
  }));
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [open, setOpen] = useState(false);
  const [disable, setDisable] = useState(true);
  const [formValue, setFormValue] = useState(initFormSite);
  const message = (
    <Message showIcon type="success" header="Success">
      You successfully added an alert to: {sme}
    </Message>
  );

  const handleEnable = () => {
    if (
      formValue.field_name &&
      formValue.severity &&
      formValue.operator &&
      formValue.notify_frequency
    ) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  };

  const onSubmit = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/insert/model/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: sme,
          model: formValue,
        }),
      });
      toaster.push(message, "topCenter");
      refresh();
      handleClose();
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
  };

  const handleOperatorSelector = () => {
    if (formValue?.field_name === "capture_datetime") {
      setOperatorSelector(
        ["offline"].map((operator) => ({
          label: operator.replace(/_/g, " "),
          value: operator,
        })),
      );
      setFormValue({ ...formValue, operator: "offline" });
    } else if (formValue?.field_name === "host_datetime") {
      setOperatorSelector(
        ["frozen_host"].map((operator) => ({
          label: operator.replace(/_/g, " "),
          value: operator,
        })),
      );
      setFormValue({ ...formValue, operator: "frozen_host" });
    } else if (formValue?.field_name === "comp_vib_status") {
      setOperatorSelector(
        ["equals"].map((operator) => ({
          label: operator.replace(/_/g, " "),
          value: operator,
        })),
      );
      setFormValue({ ...formValue, operator: "equals" });
    } else if (formValue?.field_name === "dynamic") {
      setOperatorSelector(
        ["absent_null_data"].map((operator) => ({
          label: operator.replace(/_/g, " "),
          value: operator,
        })),
      );
      setFormValue({ ...formValue, operator: "absent_null_data" });
    } else {
      setOperatorSelector(
        ["less_than", "greater_than", "equals", "delta_neg"].map(
          (operator) => ({
            label: operator.replace(/_/g, " "),
            value: operator,
          }),
        ),
      );
      setFormValue({ ...formValue, operator: "" });
    }
  };

  const getAlertPreview = () => {
    if (formValue.operator === "offline") {
      return "System Offline";
    } else if (formValue.operator === "dynamic") {
      return "Absent/Null Data";
    } else if (formValue.operator === "frozen_host") {
      return "Frozen Host Data";
    } else {
      return dataConfig.find((label) => label.pgcolumn === formValue.field_name)
        ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)
            ?.uilabel
        : formValue.field_name;
    }
  };

  const getTriggerDescription = () => {
    if (formValue.field_name && formValue.operator) {
      if (formValue.operator === "offline") {
        return `Trigger When: System is Offline within past ${formValue.threshold} hour(s).`;
      } else if (formValue.field_name === "host_datetime") {
        return `Trigger When: System has Frozen data within past ${formValue.threshold} hour(s).`;
      } else if (formValue.field_name === "comp_vib_status") {
        return `Trigger When: Compressor is ${formValue.threshold === true ? "ON" : "OFF"}!`;
      } else if (formValue.field_name === "dynamic") {
        return `Trigger When: System has occurences of Absent/Null data ${formValue.threshold} count(s).`;
      } else {
        return `Trigger When: ${dataConfig.find((label) => label.pgcolumn === formValue.field_name) ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)?.uilabel : formValue.field_name} is ${formValue.operator.replace(/_/g, " ")} · ${formValue.threshold} ${dataConfig.find((label) => label.pgcolumn === formValue.field_name) ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)?.unit : null}`;
      }
    }
    return null;
  };

  const getCurrentValue = () => {
    if (formValue.field_name === "dynamic") return null;

    if (
      formValue.operator === "greater_than" &&
      parseFloat(dataTable[formValue.field_name]) > formValue.threshold
    ) {
      return (
        <SeverityAlert severity={formValue.severity}>
          <b>
            {dataTable[formValue.field_name]}{" "}
            {dataConfig?.find(
              (label) => label.pgcolumn === formValue.field_name,
            )
              ? dataConfig.find(
                  (label) => label.pgcolumn === formValue.field_name,
                )?.unit
              : null}
          </b>
        </SeverityAlert>
      );
    } else if (
      formValue.operator === "less_than" &&
      parseFloat(dataTable[formValue.field_name]) < formValue.threshold
    ) {
      return (
        <SeverityAlert severity={formValue.severity}>
          <b>
            {dataTable[formValue.field_name]}{" "}
            {dataConfig?.find(
              (label) => label.pgcolumn === formValue.field_name,
            )
              ? dataConfig.find(
                  (label) => label.pgcolumn === formValue.field_name,
                )?.unit
              : null}
          </b>
        </SeverityAlert>
      );
    } else if (
      formValue.operator === "equals" &&
      formValue.field_name === "comp_vib_status"
    ) {
      return dataTable[formValue.field_name] ===
        (formValue.threshold.toString() === "true" ? 1 : 0) ? (
        <SeverityAlert severity={formValue.severity}>
          <b>{dataTable[formValue.field_name] === 1 ? "ON" : "OFF"}</b>
        </SeverityAlert>
      ) : (
        <SeverityAlert>
          <b>
            {dataTable[formValue.field_name] === 1 ||
            dataTable[formValue.field_name] === true
              ? "ON"
              : "OFF"}
          </b>
        </SeverityAlert>
      );
    } else if (
      formValue.operator === "offline" &&
      formValue.field_name === "capture_datetime"
    ) {
      const hours = Math.round(
        DateTime.now().diff(
          DateTime.fromISO(dataTable[formValue?.field_name]),
          "hours",
        ).hours,
      );
      return hours > parseFloat(formValue.threshold) ? (
        <SeverityAlert severity={formValue.severity}>
          {hours} Hr(s)
        </SeverityAlert>
      ) : (
        <SeverityAlert>{hours} Hr(s)</SeverityAlert>
      );
    } else if (
      formValue.operator === "frozen_host" &&
      formValue.field_name === "host_datetime"
    ) {
      const hours = Math.round(
        DateTime.now().diff(
          DateTime.fromISO(dataTable[formValue?.field_name]),
          "hours",
        ).hours,
      );
      return hours > parseFloat(formValue.threshold) ? (
        <SeverityAlert severity={formValue.severity}>
          {hours} Hr(s)
        </SeverityAlert>
      ) : (
        <SeverityAlert>{hours} Hr(s)</SeverityAlert>
      );
    } else {
      return (
        <SeverityAlert severity={"ok"}>
          {dataTable[formValue.field_name]}{" "}
          {dataConfig?.find((label) => label.pgcolumn === formValue.field_name)
            ? dataConfig.find(
                (label) => label.pgcolumn === formValue.field_name,
              )?.unit
            : null}
        </SeverityAlert>
      );
    }
  };

  useEffect(() => {
    handleEnable();
  }, [formValue]);

  useEffect(() => {
    handleOperatorSelector();
  }, [formValue.field_name]);

  return (
    <AddAlertSC>
      <Button variant="primary" size="small" onClick={handleOpen}>
        Add Default Alert
      </Button>
      <AlertModal
        title={`Add Alert To: ${sme}`}
        open={open}
        onClose={handleClose}
        onSubmit={onSubmit}
        severity={formValue.severity}
        alertPreview={getAlertPreview()}
        submitDisabled={disable}
        size="xs"
      >
        {/* Trigger Description */}
        {getTriggerDescription() && (
          <PreviewHeader
            centered={false}
            style={{ paddingTop: "12px", paddingBottom: "12px" }}
          >
            {getTriggerDescription()}
          </PreviewHeader>
        )}

        {/* Notify Frequency */}
        {formValue.notify_frequency && (
          <PreviewHeader>
            <b>Notify Every {formValue.notify_frequency} hours</b>
          </PreviewHeader>
        )}

        {/* Current Value */}
        {getCurrentValue() && (
          <div style={{ marginBottom: "16px" }}>
            Current Severity/Value: {getCurrentValue()}
          </div>
        )}

        {/* Form */}
        <Form
          fluid
          formValue={formValue}
          onChange={(formValue) => setFormValue(formValue)}
        >
          <Form.Group controlId="field_name">
            <Form.ControlLabel>Field Name</Form.ControlLabel>
            <Form.Control
              name="field_name"
              accepter={SelectPicker}
              data={fieldSelector}
              searchable={false}
              block
            />
          </Form.Group>

          {formValue.field_name === "dynamic" && (
            <Form.Group controlId="pg_table_switch">
              <Form.ControlLabel>Table</Form.ControlLabel>
              <Form.Control
                name="pg_table_switch"
                accepter={SelectPicker}
                data={pgTableSelector}
                searchable={false}
                block
              />
            </Form.Group>
          )}

          <Form.Group controlId="severity">
            <Form.ControlLabel>Severity</Form.ControlLabel>
            <Form.Control
              name="severity"
              accepter={SelectPicker}
              data={severitySelector}
              searchable={false}
              block
            />
          </Form.Group>

          <Form.Group controlId="operator">
            <Form.ControlLabel>Operator</Form.ControlLabel>
            <Form.Control
              name="operator"
              accepter={SelectPicker}
              data={operatorSelector}
              searchable={false}
              block
            />
          </Form.Group>

          {formValue.field_name === "comp_vib_status" ? (
            <Form.Group controlId="threshold">
              <Form.ControlLabel>Threshold</Form.ControlLabel>
              <Form.Control name="threshold" accepter={Toggle} block />
            </Form.Group>
          ) : (
            <Form.Group controlId="threshold">
              <Form.ControlLabel>Threshold</Form.ControlLabel>
              <Form.Control
                name="threshold"
                accepter={InputNumber}
                step={1}
                block
                postfix={
                  dataConfig?.find(
                    (label) => label.pgcolumn === formValue.field_name,
                  )
                    ? dataConfig.find(
                        (label) => label.pgcolumn === formValue.field_name,
                      )?.unit
                    : null
                }
              />
            </Form.Group>
          )}

          <Form.Group controlId="notify_frequency">
            <Form.ControlLabel>Notify Frequency</Form.ControlLabel>
            <Form.Control
              name="notify_frequency"
              accepter={InputNumber}
              step={1}
              postfix="hours"
              min={0}
              max={72}
              block
            />
          </Form.Group>
        </Form>
      </AlertModal>
    </AddAlertSC>
  );
};

const AddAlertSC = styled.div`
  display: inline-block;
  padding-left: 1%;

  .rs-input-group-addon {
    width: 60px;
    justify-content: center;
  }
`;

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);
